import React, {  useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { clientCreate } from '../../../services/client/client';
import { messageError, messageSuccess } from '../../../utils/messages';
import { routes } from '../../../utils/routes';
import './clientRegister.css';
import { getCompanyNameByName } from '../../../services/companyNames/companyNames';

function ClientRegister ({ history }) {
  const { t } = useTranslation();
  const [clientName, setClientName] = useState('');
  const [clientAlias, setClientAlias] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientFax, setClientFax] = useState('');
  const [clientContactName, setClientContactName] = useState('');
  const [clientContactLastName, setClientContactLastName] = useState('');
  const [clientPosition, setClientPosition] = useState('');
  const [clientIva, setClientIva] = useState('');
  const [clientFreeTransport, setClientFreeTransport] = useState('');
  const [clientSeeArts, setClientSeeArts] = useState('');
  const [clientLegalId, setClientLegalId] = useState('');
  const [clientCompanyNames, setClientCompanyNames] = useState([]);
  const [displayCompanyNameDialog, setDisplayCompanyNameDialog] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const hasActiveCompanyName = clientCompanyNames.some(company => company.isActive);
    if (!hasActiveCompanyName) {
      messageError(t('messageErrorAtLeastOneCompanyNameActive'));
      return;
    }
    let iva;
    (clientIva === '') ? iva = 13 : iva = clientIva;
    const client = {
      legalId: clientLegalId,
      name: clientName,
      alias: clientAlias,
      email: clientEmail,
      phone: clientPhone,
      fax: clientFax,
      contactFirstName: clientContactName,
      contactLastName: clientContactLastName,
      position: clientPosition,
      active: true,
      iva: Number(Number(iva) / 100).toFixed(2),
      freeTransport: Boolean(clientFreeTransport),
      seeArts: Boolean(clientSeeArts),
      isProspect: 0,
      companyNames: clientCompanyNames.map(name => ({
        name: name.name,
        isActive: Boolean(name.isActive)
      }))
    };
    await clientCreate(client).then(() => {
      messageSuccess(t('messageSuccessClientCreated'));
      history.push(routes.Clients);
    }).catch(error => {
      (error.response) ? messageError(error.response.data.message) : messageError(t('messageErrorClientCreated'));
    });
  };

  const cancel = () => {
    history.push(routes.Clients);
  };

  const deleteCompanyName = (row) => {
    setClientCompanyNames(clientCompanyNames.filter(item => item.id !== row.id));
  };

  const onCompanyNameSelect = (row) => {
    setDisplayCompanyNameDialog(true);
    setSelectedCompanyName(row);
  };

  const actions = (row) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          id="button-edit"
          style={{ margin: '3px' }}
          className="margin-button"
          onClick={(e) => { e.preventDefault(); onCompanyNameSelect(row); }}
        />
        {!row.id || row.id < 0 &&
            <Button
              icon="pi pi-times"
              id="button-remove"
              tooltip={t('remove')}
              style={{ margin: '3px' }}
              className="margin-button"
              onClick={(e) => { e.preventDefault(); deleteCompanyName(row); }}
            />
        }
      </>
    );
  };

  const toggleCompanyNameActive = (row) => {
    setClientCompanyNames(clientCompanyNames.map(item =>
      item.id === row.id ? { ...item, isActive: !item.isActive } : item
    ));
  };

  const saveCompanyName = async (e) => {
    e.preventDefault();

    const name = selectedCompanyName?.name?.trim();
    if (!name) {
      messageError(t('messageErrorCompanyNameRequired'));
      return;
    }

    const nameToCheck = name.toLowerCase();
    const id = selectedCompanyName?.id;

    let isDuplicate = false;

    try {
      const response = await getCompanyNameByName(nameToCheck);
      if (response?.data && (!id || response.data.id !== id)) {
        isDuplicate = true;
      }
    } catch (err) {
      isDuplicate = false;
    }
    const localDuplicate = clientCompanyNames.some(item =>
      item.name.trim().toLowerCase() === nameToCheck && (!id || item.id !== id)
    );
    if (isDuplicate || localDuplicate) {
      messageError(t('messageErrorCompanyNameDuplicate'));
      return;
    }

    let updated;

    if (id) {
      updated = clientCompanyNames.map(item =>
        item.id === id ? { ...item, name } : item
      );
    } else {
      const newCompany = {
        id: -Date.now(),
        name,
        isActive: true
      };
      updated = [...clientCompanyNames, newCompany];
    }

    setClientCompanyNames(updated);
    setDisplayCompanyNameDialog(false);
    setSelectedCompanyName(null);
  };

  return (
    <div>
      <div className="ui-g ui-fluid" >
        <div className="ui-g-12">
          <div className="card-user">
            <div className="card">
              <div className="content-section introduction">
                <h1>{t('clientRegister')}</h1>
                <form autoComplete="off" onSubmit={handleSubmit}>
                  <div className="p-grid">
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('companyName')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <InputText
                        required
                        id='name'
                        value={clientName}
                        placeholder={t('companyName')}
                        className="input"
                        onChange={(event) => setClientName((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('legalId')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <InputText
                        id='legalId'
                        value={clientLegalId}
                        placeholder={t('legalId')}
                        className="input"
                        onChange={(event) => setClientLegalId((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('alias')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <InputText
                        required
                        id='alias'
                        value={clientAlias}
                        placeholder={t('alias')}
                        className="input"
                        onChange={(event) => setClientAlias((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('email')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <InputText
                        required
                        id='email'
                        value={clientEmail}
                        placeholder={t('email')}
                        className="input"
                        onChange={(event) => setClientEmail((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('contactName')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-4">
                      <InputText
                        id='contactName'
                        value={clientContactName}
                        placeholder={t('contactName')}
                        className="input"
                        onChange={(event) => setClientContactName((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('contactLastName')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-4">
                      <InputText
                        id='contactLastName'
                        value={clientContactLastName}
                        placeholder={t('contactLastName')}
                        className="input"
                        onChange={(event) => setClientContactLastName((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('phone')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-4">
                      <InputText
                        id='phone'
                        value={clientPhone}
                        placeholder={t('phone')}
                        className="input"
                        onChange={(event) => setClientPhone((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('fax')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-4">
                      <InputText
                        id='fax'
                        value={clientFax}
                        placeholder={t('fax')}
                        className="input"
                        onChange={(event) => setClientFax((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('position')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <InputText
                        id='position'
                        value={clientPosition}
                        placeholder={t('position')}
                        className="input-phone"
                        onChange={(event) => setClientPosition((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('iva')} (%)</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <InputText
                        keyfilter="pnum"
                        id='iva'
                        value={clientIva}
                        placeholder={t('iva')}
                        className="input-phone"
                        onChange={(event) => setClientIva((event.target.value))}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('freeTransport')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <Checkbox
                        id="freeTransport"
                        style={{marginTop: '15px'}}
                        onChange={(e) => setClientFreeTransport(e.checked)}
                        checked={Boolean(clientFreeTransport)}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h5>{t('seeArts')}</h5></div>
                    <div className="p-col-8 p-md-8 p-lg-10">
                      <Checkbox
                        id="seeArts"
                        style={{marginTop: '15px'}}
                        onChange={(e) => setClientSeeArts(e.checked)}
                        checked={Boolean(clientSeeArts)}
                      />
                    </div>
                    <div className="p-col-4 p-md-4 p-lg-2"><h3>{t('legalNames')}</h3></div>
                    <div className="p-col-8 p-md-8 p-lg-10" style={{textAlign: 'right'}}><Button
                      id='button-add-company-name'
                      type="button"
                      icon="pi pi-plus"
                      label={t('addCompanyName')}
                      style={{ margin: '10px', width: 'auto' }}
                      onClick={() => setDisplayCompanyNameDialog(true)}
                    /></div>
                    <DataTable value={clientCompanyNames} className='p-lg-12' responsive={true} >
                      <Column field="name" header={t('name')} />
                      <Column field="active" header={t('active')} style={{ textAlign: 'center', width: '200px' }} body={
                        rowData =>
                          <Checkbox
                            inputId="isCompanyNameActive"
                            id="isCompanyNameActive"
                            onChange={() => toggleCompanyNameActive(rowData)}
                            checked={Boolean(rowData.isActive)}
                          />} />
                      <Column style={{ textAlign: 'center', width: '200px' }} body={actions} header={t('actions')} />
                    </DataTable>
                    <Dialog visible={displayCompanyNameDialog}
                      style={{ width: '80vw', maxWidth: '700px' }}
                      footer={
                        <div className="ui-dialog-buttonpane p-clearfix">
                          <Button
                            id="button-save-company-name"
                            label={t('save')}
                            icon="pi pi-check"
                            onClick={saveCompanyName}
                          />
                        </div>
                      } header={t('legalName')}
                      modal={true} onHide={() => setDisplayCompanyNameDialog(false)}>

                      <div className="p-grid p-fluid">
                        <div className="p-col-4" style={{ padding: '.75em' }}>
                          <label htmlFor="companyNameDialog">{t('name')}</label>
                        </div>
                        <div className="p-col-8" style={{ padding: '.5em' }}>
                          <InputText id="companyNameDialog"
                            onChange={e => setSelectedCompanyName({ ...selectedCompanyName, name: e.target.value })}
                            value={selectedCompanyName?.name ?? ''} />
                        </div>
                      </div>
                    </Dialog>
                  </div>
                  <div className="ui-button-group">
                    <Button
                      id='button-save'
                      icon="pi pi-save"
                      label={t('save')}
                      style={{ marginTop: '10px', width: 'auto' }}
                    />
                    <Button
                      id='button-return'
                      type="button"
                      icon="pi pi-times"
                      label={t('cancel')}
                      style={{ margin: '10px', width: 'auto' }}
                      onClick={cancel}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default ClientRegister;
